// Filter chips and micro-tags (DESIGN_SPEC §9 legend, §10.6).
//   <ChipGroup label>{chips}</ChipGroup>                         .legend, role=group
//   <Chip pressed onClick mark? glyph? count? all? {...button attrs}>name</Chip>
//       the legend IS the filter: mark + name + count; unpressed → hollow mark + faint text; `all` gets the rule-strong box
//       callers read e.shiftKey in onClick to solo a category
//   <MicroTag>email-attachment</MicroTag> · <MicroTags items />   square outlined micro-tag (.tag): ONLY user labels, the `url` kind, `input on`
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from "react";
import { CatMark, type Category } from "./Glyphs";
import { cx } from "./cx";

export function ChipGroup({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={cx("legend", className)} role="group" aria-label={label}>
      {children}
    </div>
  );
}

type ChipProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-pressed"> & {
  pressed: boolean;
  mark?: Category;
  glyph?: ReactNode;
  count?: ReactNode;
  all?: boolean;
};

export function Chip({ pressed, mark, glyph, count, all, className, children, type = "button", ...rest }: ChipProps) {
  return (
    <button type={type} className={cx("chip", all && "chip--all", className)} aria-pressed={pressed} {...rest}>
      {mark && <CatMark c={mark} hollow={!pressed} />}
      {glyph}
      {children}
      {count !== undefined && count !== null && <span className="ct">{count}</span>}
    </button>
  );
}

export function MicroTag({
  className,
  children,
  href,
  onClick,
  title,
}: {
  className?: string;
  children: ReactNode;
  // When set, the tag renders as a link / button so a click filters the
  // list by this tag's value (e.g. family chip on a JobRow → /jobs?q=NanoCore).
  // onClick receives the event so a row's click handler can stopPropagation.
  href?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  title?: string;
}) {
  const text = typeof children === "string" ? children : undefined;
  const inner = <span className="min-w-0 truncate">{children}</span>;
  if (href) {
    return (
      <a className={cx("tag", "tag--link", className)} href={href} title={title ?? text} onClick={onClick}>
        {inner}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={cx("tag", "tag--link", className)} title={title ?? text} onClick={onClick}>
        {inner}
      </button>
    );
  }
  // a 64-character label must not widen a phone page: the chip ellipsizes, its title holds the whole label
  return (
    <span className={cx("tag", className)} title={title ?? text}>
      {inner}
    </span>
  );
}

export function MicroTags({ items, className }: { items: readonly string[]; className?: string }) {
  if (!items.length) return null;
  return (
    <span className={cx("tags", className)}>
      {items.map((t) => (
        <MicroTag key={t}>{t}</MicroTag>
      ))}
    </span>
  );
}
